import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Footer } from './Footer';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const { t } = useTranslation('application');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = 'help-modal-title';
  
  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousFocusRef.current = document.activeElement as HTMLElement;
      
      // Focus the modal when it opens
      const firstFocusable = modalRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;
      firstFocusable?.focus();
      
      // Trap focus within modal
      const handleTabKey = (e: KeyboardEvent) => {
        if (!modalRef.current) return;
        
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement?.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement?.focus();
            }
          }
        }
        
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleTabKey);
      
      return () => {
        document.removeEventListener('keydown', handleTabKey);
        // Restore focus to previously focused element
        previousFocusRef.current?.focus();
      };
    }
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal Content - Full Screen with Everything Scrollable */}
      <div ref={modalRef} className="relative bg-white w-full h-full overflow-y-auto">
        {/* Header */}
        <div className="bg-[#131313] p-6 flex items-center justify-between sticky top-0 z-10">
          <h2 id={titleId} className="text-white">
            {t('help.modalTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-[#FF6F4E] transition-colors p-2"
            aria-label="Close help modal"
          >
            <X className="size-8" />
          </button>
        </div>
        
        {/* All Content in Scrollable Area */}
        <div className="p-6 md:p-8">
          <div className="space-y-8 max-w-3xl mx-auto">
            {/* FAQ Item 1 */}
            <div className="space-y-3">
              <h3 className="text-[rgb(38,15,8)]">
                {t('help.faq.q1.question')}
              </h3>
              <p>
                {t('help.faq.q1.answer')}
              </p>
            </div>

            {/* FAQ Item 2 */}
            <div className="space-y-3">
              <h3 className="text-[rgb(30,30,30)]">
                {t('help.faq.q2.question')}
              </h3>
              <p>
                {t('help.faq.q2.answer')}
              </p>
            </div>

            {/* FAQ Item 3 */}
            <div className="space-y-3">
              <h3 className="text-[rgb(30,30,30)]">
                {t('help.faq.q3.question')}
              </h3>
              <p>
                {t('help.faq.q3.answer')}
              </p>
            </div>

            {/* FAQ Item 4 */}
            <div className="space-y-3">
              <h3 className="text-[rgb(30,30,30)]">
                {t('help.faq.q4.question')}
              </h3>
              <p>
                {t('help.faq.q4.answer')}
              </p>
            </div>

            {/* FAQ Item 5 */}
            <div className="space-y-3">
              <h3 className="text-[rgb(30,30,30)]">
                {t('help.faq.q5.question')}
              </h3>
              <p>
                {t('help.faq.q5.answer')}
              </p>
            </div>

            {/* Contact Section */}
            <div className="bg-[#FBFBFB] p-6 rounded space-y-3 border-l-4 border-[#0BAB47]">
              <h3 className="text-[#131313]">
                {t('help.contact.title')}
              </h3>
              <p>
                {t('help.contact.description')}
              </p>
              <p>
                <a 
                  href={`mailto:${t('help.contact.email')}`}
                  className="text-[#4D2200] underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('help.contact.email')}
                </a>
              </p>
            </div>

            {/* Close Button */}
            <div className="flex justify-center py-6">
              <button
                onClick={onClose}
                className="bg-[#FF6F4E] text-[#131313] hover:bg-[#FF6F4E]/90 transition-colors font-bold text-[16px] px-6 py-3"
              >
                {t('help.closeButton')}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}
